import {
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { normalizeApiBaseUrl } from "../utils/urlUtils";

// Backend API configuration - prioritize environment variables over defaults
export const BACKEND_API_URI = normalizeApiBaseUrl(
  import.meta.env.REACT_APP_BACKEND_API_URI ||
  (import.meta.env.PROD
    ? "https://wa-backend-cvportal-francent-test-g0e9cmc3d5hjfhge.francecentral-01.azurewebsites.net/" // Production fallback
    : "http://localhost:8888/") // Development fallback
);

export const REACT_APP_AI_API_BASE_URL = normalizeApiBaseUrl(
  import.meta.env.REACT_APP_AI_API_BASE_URL ||
  (import.meta.env.PROD
    ? "https://your-ai-api-production-url/api" // Production fallback
    : "http://localhost:7292/api") // Development fallback
);

// Determine the correct redirect URI based on environment
const getAppBaseRedirectUri = () => {
  // Check if we're running in production
  if (window.location.hostname !== "localhost") {
    // First try to use environment variable
    if (import.meta.env.REACT_APP_MSAL_REDIRECT_URI) {
      return import.meta.env.REACT_APP_MSAL_REDIRECT_URI;
    }

    // Fallback to window location
    // Get the base path in case the app is deployed in a subdirectory
    const basePath =
      document.getElementsByTagName("base")[0]?.getAttribute("href") || "/";
    const redirectUri = `${window.location.origin}${basePath}`.replace(
      /\/$/,
      "/"
    );
    return redirectUri;
  }

  // For local development
  return import.meta.env.REACT_APP_MSAL_REDIRECT_URI || "http://localhost:3000/";
};

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.REACT_APP_MSAL_CLIENT_ID || "",
    authority: `https://login.microsoftonline.com/${import.meta.env.REACT_APP_MSAL_TENANT_ID}`,
    redirectUri: getAppBaseRedirectUri(),
    postLogoutRedirectUri: getAppBaseRedirectUri(),
  },
  cache: {
    cacheLocation: "localStorage", // Changed from sessionStorage to support multi-tab authentication
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
const msalInit = msalInstance.initialize();

// Export the initialized instance promise
export const getMsalInstance = () => msalInit.then(() => msalInstance);

// Debug helper to get access token - only available in development
export const debugGetAccessToken = async (
  msalInstance: PublicClientApplication
) => {
  if (import.meta.env.DEV) {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        console.error("[Debug] No accounts found. Please login first.");
        return null;
      }

      const response = await msalInstance.acquireTokenSilent({
        scopes: apiScopes.backendApi,
        account: accounts[0],
      });
      console.log("[Debug] Access Token:", response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error("[Debug] Error getting token:", error);
      // If silent token acquisition fails, try interactive
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: apiScopes.backendApi,
        });
        console.log(
          "[Debug] Access Token (after popup):",
          response.accessToken
        );
        return response.accessToken;
      } catch (popupError) {
        console.error("[Debug] Popup error:", popupError);
        return null;
      }
    }
  }
  return null;
};

// Expose debug utilities in development
if (import.meta.env.DEV) {
  (window as any).__msal = {
    instance: msalInstance,
    getToken: async () => debugGetAccessToken(msalInstance),
  };
  console.log(
    "[Debug] MSAL debug utilities available. Use window.__msal.getToken() to get access token"
  );
}

// Only log environment variables in development mode if needed for debugging
if (
  import.meta.env.DEV &&
  import.meta.env.REACT_APP_DEBUG_MSAL === "true"
) {
  console.log("[Environment] MODE:", import.meta.env.MODE);
  console.log(
    "[Environment] MSAL Client ID:",
    import.meta.env.REACT_APP_MSAL_CLIENT_ID
  );
  console.log(
    "[Environment] MSAL Tenant ID:",
    import.meta.env.REACT_APP_MSAL_TENANT_ID
  );
  console.log(
    "[Environment] MSAL Redirect URI:",
    import.meta.env.REACT_APP_MSAL_REDIRECT_URI
  );
  console.log("[Environment] Backend API URI:", BACKEND_API_URI);
  console.log("API Scope used:", import.meta.env.REACT_APP_AZURE_API_SCOPE);
}

// Admin group checking flag
// export const ENABLE_ADMIN_CHECK = import.meta.env.REACT_APP_ENABLE_ADMIN_CHECK === "true";
export const ENABLE_ADMIN_CHECK = true;

// Admin group ID - this should be the actual Object ID of the group from Azure AD
// for SopraSteria there is no admin group, so we use a dummy value
export const ADMIN_GROUP_ID = import.meta.env.REACT_APP_ADMIN_GROUP_ID || "";

// API scopes configuration
export const apiScopes = {
  userProfile: ["User.Read"],
  groups: ["User.Read"],
  // Using the scope name defined in your Azure AD app registration - filter for default scope and type compatible with msal
  backendApi: [import.meta.env.REACT_APP_AZURE_API_SCOPE].filter(
    Boolean
  ) as string[],
};
